/**
 * @modified November 13th, 2020
 * @author Sebastian Inman <sinman@nicindustries.com>
 * @summary Each format method makes use of the "Normalize" method. By normalizing the values being returned by
 * the string formatters, we ensure that the Unicode version of each character is being returned. We've run into
 * issues in the past with non-unicode characters making their way through the front-end into MAS and causing
 * some issues. This is a basic attempt to minimize the frequency of non-unicode characters sneaking their
 * way into places they shouldn't be. Please treat this as a sort of first line of defense - this isn't a
 * 100% effective method, but it should at least help.
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/normalize
 */

 export default {

  methods: {


    /**
     * ====================================
     * @namespace FORMATTING_HELPER_METHODS
     * ====================================
     * @summary All methods below are helper methods that perform generic formatting
     * that can be reused and implemented by the more specific validation methods
     * found further down the file.
     */


    /**
     * @method removeTrailingWhitespace
     * @param {string} value
     * @description Removes any trailing whitespace from the string value.
     * This method should only be called on blur events.
     */

    removeTrailingWhitespace(value) {

      value = value.replace(/\s+$/g, '');

      return value;

    },


    /**
     * @method formatWhitespace
     * @param {string} value The string value being formatted.
     * @description Formats a string value to disallow multiple
     * spaces being places in succession. Also prevents the string
     * value from starting with a space - must begin with a character.
     */

    formatWhitespace(value) {

      // Only allow one space at a time.
      value = value.replace(/\s+/g, ' ');

      // Value can't start with a space.
      value = value.replace(/^\s/g, '');

      return value;

    },


    /**
     * @method removeSpecialCharacters
     * @param {string} value The string value being formatted.
     * @param {RegExp} pattern Pattern defining what special characters will be removed.
     * @description Remove unwanted special characters from the string.
     */

    removeSpecialCharacters(value, pattern = /(?=[/])|[^a-zA-Z0-9\s-]/g) {

      // Remove any characters that are not: a-z, A-Z, 0-9, space, forward slash, or a dash.
      value = value.replace(pattern, '');

      return value;

    },


    /**
     * @method removeNonLetters
     * @param {string} value The string value being formatted.
     */

    removeNonLetters(value) {

      // Remove all non-letter characters.
      value = value.replace(/[^a-zA-Z]/g, '');

      return value;

    },


    /**
     * @method removeNonNumbers
     * @param {string} value The string value being formatted.
     */

    removeNonNumbers(value) {

      // Remove all non-number characters.
      value = value.replace(/[^0-9]/g, '');

      return value;

    },


    /**
     * ====================================
     * @namespace VALIDATION_HELPER_METHODS
     * ====================================
     * All methods below are for formatting and validating specific types
     * of input & string values.
     */


    /**
     * @method formatName
     * @param {string} value The string value being formatted.
     * @param {boolean} normalize Should the output be normalized?
     */

    formatName(value, normalize = true) {

      value = this.removeSpecialCharacters(value, /(?=[/])|[^a-zA-Z0-9\s-.]/g);
      value = this.formatWhitespace(value);

      return normalize ? value.normalize('NFC') : value;

    },


    /**
     * @method formatPhone
     * @param {string} value The string value being formatted.
     * @param {boolean} normalize Should the output be normalized?
     * @description Formats phone number strings to be a consistent,
     * dash-separated phone number by replacing all spaces and periods
     * with dashes, and only allowing digit characters to be entered.
     * @example 1-234-567-8900
     * @example 123-456-7890
     * @example 1234567890
     */

    formatPhone(value, normalize = true) {

      // Use dashes instead of spaces or periods.
      value = value.replace(/[\s.]/g, '-');

      // Only allow one dash at a time.
      value = value.replace(/-+/g, '-');

      // Value can't start with a dash.
      value = value.replace(/^-/g, '');

      // Only allow digits and dashes.
      value = value.replace(/[^\d-]/gm, '');

      return normalize ? value.normalize('NFC') : value;

    },


    /**
     * @method formatAddress
     * @param {string} value The string value being formatted.
     * @param {boolean} normalize Should the output be normalized?
     * @description Formats address strings to not allow the value
     * to begin with a space, or contain multiple spaces alongside
     * one another.
     */

    formatAddress(value, normalize = true) {

      value = this.removeSpecialCharacters(value, /(?=[/])|[^a-zA-Z0-9\s-#.]/g);
      value = this.formatWhitespace(value);

      return normalize ? value.normalize('NFC') : value;

    },


    /**
     * @method formatPostalCode
     * @param {string} value The string value being formatted.
     * @param {boolean} normalize Should the output be normalized?
     */

    formatPostalCode(value, normalize = true) {

      value = this.removeSpecialCharacters(value);
      value = this.formatWhitespace(value);

      return normalize ? value.normalize('NFC') : value;

    },


    /**
     * @method formatCCNumber
     * @param {string} value The string value being formatted.
     * @param {boolean} normalize Should the output be normalized?
     */

    formatCCNumber(value, normalize = true) {

      value = this.removeNonNumbers(value);

      return normalize ? value.normalize('NFC') : value;

    },


    /**
     * @method formatCVV
     * @param {string} value The string value being formatted.
     * @param {boolean} normalize Should the output be normalized?
     */

    formatCVV(value, normalize = true) {

      value = this.removeSpecialCharacters(value);
      value = this.removeNonNumbers(value);

      return normalize ? value.normalize('NFC') : value;

    }


  }

}
